import React, {useState, useEffect, useRef, Ref, RefObject} from 'react';
import cx from 'classnames';
import Link from '../link/Link';
import {getMessage} from '../message/Message';
import {AutocompleteProps, MaxCounts} from './interfaces';
import {AutocompleteResult} from '../../redux/reducers/search.reducer';
import {keyCodes} from '../../constants';

import styles from './LEGACY_SearchBar.pcss';
import {SearchResults} from '../../types';

interface KeyDownOptions {
    clearSearchBar: Function;
    focusedIndex: number;
    inputRef: React.RefObject<HTMLInputElement> | null;
    maxIndex: number;
    setFocusedIndex: Function;
}

interface KeyDownProps extends KeyDownOptions {
    e: React.KeyboardEvent<HTMLElement>;
}

interface ResultOptions {
    focusedIndex: number;
    text: string;
}

interface FocusInstructionsOptions {
    focusedIndex: number;
    keyCode: number;
    maxIndex: number;
    shiftKey: boolean;
}

interface FocusInstructions {
    newIndex: number;
    shouldExitList: boolean;
    shouldFocusInput: boolean;
    shouldFocusResult: boolean;
}

interface ResultListProps {
    searchResults: SearchResults;
    resultOptions: ResultOptions;
}

const getProductRoute = (result: AutocompleteResult): string => `/${result.path}/${result.urlSlug}`;

const getResultsRoute = (query: string): string => `/store-search?q=${query}`;

const sortAlphabetically = (a, b) => {
    const titleA = a.title.toLowerCase();
    const titleB = b.title.toLowerCase();
    if (titleA < titleB) return -1;
    if (titleA > titleB) return 1;
    return 0;
};

const renderFormattedResult = (query: string, result: string): React.ReactNode => {
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedQuery, 'gi');
    const boldString: string = result.replace(regex, '/n$&/n');
    return boldString.split('/n').map((substring, i) => {
        if (substring.match(regex)) {
            return <strong key={i}>{substring}</strong>;
        }
        return substring;
    });
};

const getFocusChange = (keyCode: number, shiftKey: boolean): number => {
    const isForwardsTab: boolean = keyCode === keyCodes.TAB && !shiftKey;
    const isBackwardsTab: boolean = keyCode === keyCodes.TAB && shiftKey;
    const shouldDecrement: boolean = keyCode === keyCodes.UP || isBackwardsTab;
    const shouldIncrement: boolean = keyCode === keyCodes.DOWN || isForwardsTab;
    if (!shouldDecrement && !shouldIncrement) return 0;
    return shouldIncrement ? 1 : -1;
};

const getFocusInstructions = ({
    focusedIndex,
    keyCode,
    maxIndex,
    shiftKey
}: FocusInstructionsOptions): FocusInstructions => {
    const change: number = getFocusChange(keyCode, shiftKey);
    const exceedingMax: boolean = focusedIndex >= maxIndex && change > -1;
    const shouldFocusInput: boolean = focusedIndex === 0 && change === -1;
    const shouldExitList: boolean = keyCode === keyCodes.TAB && exceedingMax;
    const shouldFocusResult: boolean = !exceedingMax && maxIndex > 0;
    const newIndex: number = focusedIndex + change;
    return {
        newIndex,
        shouldExitList,
        shouldFocusInput,
        shouldFocusResult
    };
};

const handleKeyDown = (props: KeyDownProps): void => {
    const {clearSearchBar, e, focusedIndex, inputRef, maxIndex, setFocusedIndex} = props;

    if (e.keyCode === keyCodes.ESCAPE) {
        clearSearchBar();
        return;
    }

    if (e.keyCode === keyCodes.UP || e.keyCode === keyCodes.DOWN) {
        e.preventDefault();
    }

    const {
        newIndex,
        shouldExitList,
        shouldFocusInput,
        shouldFocusResult
    }: FocusInstructions = getFocusInstructions({
        focusedIndex,
        keyCode: e.keyCode,
        maxIndex,
        shiftKey: e.shiftKey
    });

    // Tab out of autocomplete list to close button
    if (shouldExitList) {
        setFocusedIndex(-1);
        return;
    }

    // Focus back up to search input
    if (shouldFocusInput) {
        inputRef && inputRef.current && inputRef.current.focus();
        setFocusedIndex(-1);
        return;
    }

    // Focus next item up or down
    if (shouldFocusResult) {
        setFocusedIndex(newIndex);
    }
};

const matchRef = (
    focusedIndex: number,
    index: number,
    ref: RefObject<HTMLInputElement>
): Ref<HTMLInputElement> | null => (focusedIndex === index ? ref : null);

const EmptyLink = React.forwardRef(
    (props: {resultOptions: ResultOptions; messages: any}, ref: Ref<HTMLInputElement>) => {
        const {resultOptions, messages} = props;
        const {text} = resultOptions;
        const emptySearchText: string = getMessage(messages, 'diesel.common.search.searchFor', [
            text
        ]);

        return (
            <Link
                to={getResultsRoute(text)}
                className={styles.autoCompleteResult}
                key={'autocomplete-empty'}
                ariaLabel={emptySearchText}
                // TODO get rid of cast to any
                ref={(ref as any) as React.LegacyRef<HTMLInputElement>}>
                <div className={styles.resultText}>{emptySearchText}</div>
            </Link>
        );
    }
);

const SeeMoreLink = React.forwardRef(
    (
        props: {resultOptions: ResultOptions; maxSuggestions: number; messages: any},
        ref: Ref<HTMLInputElement>
    ) => {
        const {resultOptions, messages} = props;
        const {text} = resultOptions;
        const seeMoreText: string = getMessage(messages, 'diesel.common.search.autocompleteLink');
        return (
            <Link
                to={getResultsRoute(text)}
                className={cx(styles.autoCompleteResult, styles.seeMore)}
                key={'autocomplete-seemore'}
                ariaLabel={seeMoreText}
                // TODO get rid of cast to any
                ref={ref as any}>
                <div className={styles.resultText}>{seeMoreText}</div>
            </Link>
        );
    }
);

const ResultList = React.forwardRef((props: ResultListProps, ref: Ref<HTMLInputElement>) => {
    const {searchResults, resultOptions} = props;
    const {focusedIndex, text} = resultOptions;
    return searchResults.map((result, i) => (
        <Link
            to={getProductRoute(result)}
            className={styles.autoCompleteResult}
            key={`autocomplete-${result.title}`}
            ariaLabel={result.title}
            // TODO get rid of cast to any
            ref={matchRef(focusedIndex, i, ref as RefObject<HTMLInputElement>) as any}>
            <div className={styles.resultText}>{renderFormattedResult(text, result.title)}</div>
        </Link>
    ));
});

const Autocomplete = React.forwardRef(
    (props: AutocompleteProps & {customWidth?: number}, inputRef: Ref<HTMLInputElement> | null) => {
        const {
            clearSearchBar,
            customWidth,
            maxCounts,
            maxSuggestions,
            messages,
            searchResults = [],
            text
        } = props;
        const {maxIndex}: MaxCounts = maxCounts;
        const shouldRenderSearchResults = !!(searchResults && searchResults.length);
        const showSeeMore = !!(searchResults && searchResults.length > maxSuggestions);
        const focusedRef = useRef<HTMLInputElement>(null);
        const [focusedIndex, setFocusedIndex] = useState<number>(-1);
        const [sortedSearchResults, setSortedSearchResults] = useState<AutocompleteResult[]>([]);

        useEffect(() => {
            if (!shouldRenderSearchResults) {
                setSortedSearchResults([]);
                return;
            }
            const tempSortedSearchResults = searchResults;
            setSortedSearchResults(tempSortedSearchResults.sort(sortAlphabetically));
        }, [props.searchResults]);

        useEffect(() => {
            focusedRef.current && focusedRef.current.focus();
            if (props.triggerFocus && focusedIndex === -1) {
                setFocusedIndex(0);
            }
        }, [focusedIndex, props.triggerFocus]);

        const resultOptions: ResultOptions = {
            text,
            focusedIndex
        };

        const keyDownOptions: KeyDownOptions = {
            clearSearchBar,
            focusedIndex,
            inputRef: inputRef as RefObject<HTMLInputElement>,
            maxIndex,
            setFocusedIndex
        };

        return (
            <div // eslint-disable-line jsx-a11y/no-noninteractive-element-interactions
                className={styles.searchAutocomplete}
                style={customWidth ? {width: customWidth} : {}}
                role="navigation"
                onFocus={() => {
                    focusedIndex === -1 && setFocusedIndex(0);
                }}
                onKeyDown={e => handleKeyDown({...keyDownOptions, e})}>
                <div className={styles.resultsWrapper}>
                    {sortedSearchResults.length ? (
                        <ResultList
                            searchResults={sortedSearchResults}
                            resultOptions={resultOptions}
                            ref={focusedRef}
                        />
                    ) : (
                        <EmptyLink
                            resultOptions={resultOptions}
                            messages={messages}
                            ref={matchRef(focusedIndex, 0, focusedRef)}
                        />
                    )}
                </div>
                {showSeeMore && (
                    <SeeMoreLink
                        resultOptions={resultOptions}
                        messages={messages}
                        maxSuggestions={maxSuggestions}
                        ref={matchRef(focusedIndex, maxSuggestions, focusedRef)}
                    />
                )}
            </div>
        );
    }
);

export default Autocomplete;
